import { useTranslations } from '@vocab/react';
import { Box, IconSearch, Inline, Stack, Text } from 'braid-design-system';

import { TextLink } from 'src/components/NavLink/NavLink';
import { getRefineSearchQuery } from 'src/components/Search/utils/utils';
import { useSearchParams } from 'src/modules/seo-header/search-results-page/search-params';
import { useSearchQueryCorrectionFeatureFlag } from 'src/store/featureFlags/hooks';
import { useDispatch, useSelector } from 'src/store/react';
import { removeQueryHintsParam } from 'src/store/results';
import { updateBaseKeywordParam } from 'src/store/search';
import {
  selectClearCompanyRefinements,
  selectCompanySuggestion,
  selectResults,
  selectIntentSuggestions,
  selectIsQueryModified,
  selectSearchQuery,
} from 'src/store/selectors';
import type {
  SearchViewModelHeader,
  SearchViewModelSuggestions,
} from 'src/types/globals';

import CompanyResults from '../CompanyResults/CompanyResults';
import CompanySuggestion, {
  BroadenCompanySearch,
} from '../CompanySuggestion/CompanySuggestion';
import DidYouMean from '../DidYouMean/DidYouMean';
import SearchResultsDistance from '../SearchResultDistance/SearchResultsDistance';
import SearchResultSummary from '../SearchResultSummary/SearchResultSummary';

import translations from './.vocab';

const paddingY = 'large';

export const SearchResultsHeaderSpacer = () => (
  <Box component="span" display="block" padding={paddingY} aria-hidden>
    <Text size="small">&nbsp;</Text>
  </Box>
);

interface SearchResultsDistanceHeadersProps {
  shouldShowDistanceHeader: boolean | undefined;
  header: SearchViewModelHeader | null | undefined;
  locationSuggestions: SearchViewModelSuggestions | null | undefined;
}

const SearchResultsHeaders = ({
  shouldShowDistanceHeader,
  header,
  locationSuggestions,
}: SearchResultsDistanceHeadersProps) => {
  const dispatch = useDispatch();
  const searchResultsViewModel = useSelector(selectResults);
  const companySuggestion = useSelector(selectCompanySuggestion);
  const clearCompanyRefinements =
    useSelector(selectClearCompanyRefinements) || false;

  const { searchParams, jobs } = searchResultsViewModel || {};
  const { companyName, keywords: suggestCompanyAsKeywords } =
    companySuggestion?.search || {};

  const isCompanySearch = Boolean(searchParams && searchParams.companysearch);
  const showCompanyResults =
    typeof clearCompanyRefinements === 'object' && !isCompanySearch;

  const { tags } = useSearchParams();
  const isNewToYou = tags === 'new';
  const isSearchQueryCorrectionEnabled = useSearchQueryCorrectionFeatureFlag();
  const isQueryModified = useSelector(selectIsQueryModified);
  const intentSuggestions = useSelector(selectIntentSuggestions);
  const searchQuery = getRefineSearchQuery(useSelector(selectSearchQuery));
  const { t } = useTranslations(translations);
  const intentSuggestionsKeywords = intentSuggestions?.[0]?.params?.keywords;
  const intentSuggestionsLabelDefaultText =
    intentSuggestions?.[0]?.label?.defaultText;

  return (
    <Box paddingY={paddingY}>
      <Stack space="medium">
        <SearchResultSummary key="search-result-summary" />
        {!isNewToYou && (
          <>
            {isSearchQueryCorrectionEnabled &&
            isQueryModified &&
            intentSuggestions ? (
              <>
                {intentSuggestions[0].count > 0 &&
                intentSuggestionsLabelDefaultText ? (
                  <Inline space="none" alignY="center">
                    <Box paddingRight="xsmall">
                      <Text size="small">
                        <IconSearch />
                      </Text>
                    </Box>
                    <Text
                      data={{ automation: 'searchInsteadForText' }}
                      size="small"
                    >
                      {t('Search instead for...', {
                        keywords: intentSuggestionsLabelDefaultText,
                        TextLink: (children) => (
                          <TextLink
                            data={{ automation: 'searchInsteadForLink' }}
                            location={{
                              pathname: '/jobs',
                              query: {
                                ...searchQuery,
                                keywords: intentSuggestionsKeywords,
                              },
                            }}
                            onClick={() => {
                              if (intentSuggestionsKeywords) {
                                dispatch(
                                  updateBaseKeywordParam(
                                    intentSuggestionsKeywords,
                                  ),
                                );
                              }
                              dispatch(removeQueryHintsParam());
                              // Remove the queryHints param from the URL to stop
                              // the search query correction from being triggered
                            }}
                          >
                            {children}
                          </TextLink>
                        ),
                      })}
                    </Text>
                  </Inline>
                ) : (
                  <Inline space="none" alignY="center">
                    <Box paddingRight="xsmall">
                      <Text size="small">
                        <IconSearch />
                      </Text>
                    </Box>
                    <Text
                      data={{ automation: 'noResultsForText' }}
                      size="small"
                    >
                      {t('No search results for...', {
                        intentSuggestionsKeywords:
                          intentSuggestionsKeywords || '',
                      })}
                    </Text>
                  </Inline>
                )}
              </>
            ) : null}
            {companyName ? (
              <CompanySuggestion
                companyName={companyName}
                suggestionCount={companySuggestion?.count}
                searchParams={searchParams}
              />
            ) : null}
            {shouldShowDistanceHeader ||
            locationSuggestions ||
            suggestCompanyAsKeywords ||
            showCompanyResults ? (
              <Stack space="medium">
                {shouldShowDistanceHeader && !showCompanyResults ? (
                  <SearchResultsDistance message={header} />
                ) : null}
                {locationSuggestions ? (
                  <DidYouMean locations={locationSuggestions} />
                ) : null}
                {suggestCompanyAsKeywords ? (
                  <BroadenCompanySearch
                    keywords={suggestCompanyAsKeywords}
                    suggestionCount={companySuggestion?.count}
                    searchParams={searchParams}
                  />
                ) : null}
                {showCompanyResults ? (
                  <CompanyResults
                    isCompanySearch={isCompanySearch}
                    clearCompanyRefinements={clearCompanyRefinements}
                    jobs={jobs || []}
                  />
                ) : null}
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default SearchResultsHeaders;
